import * as Sentry from '@sentry/react'

import { getErrorContext } from '@/shared/error-context/store'

export type UIErrorContext = {
	source:
		| 'component'
		| 'hook'
		| 'state'
		| 'router'
		| 'react-query'
		| 'unknown'
		| 'clerk'
	feature?: string
	component?: string
	action?: string
	section?: string
	entityId?: string
	metadata?: Record<string, string | number | boolean>
}

/**
 * Capture UI errors with context tags for Sentry grouping
 *
 * @example
 * captureException(error, {
 *   source: 'component',
 *   feature: 'alerts',
 *   component: 'AlertsTable',
 *   action: 'load_data'
 * })
 */
export function captureException(
	error: Error | unknown,
	context: UIErrorContext,
	level: Sentry.SeverityLevel = 'error',
): void {
	const errorObj = error instanceof Error ? error : new Error(String(error))

	// Auto-fill missing fields from error context store
	const storeContext = getErrorContext()
	const feature = context.feature ?? storeContext.feature
	const section = context.section ?? storeContext.section
	const entityId = context.entityId ?? storeContext.entityId
	const metadata = context.metadata ?? storeContext.metadata

	Sentry.withScope((scope) => {
		scope.setLevel(level)
		scope.setTag('error.source', context.source)
		scope.setTag('error.type', 'ui')

		if (feature) scope.setTag('feature', feature)
		if (context.component) scope.setTag('component', context.component)
		if (context.action) scope.setTag('action', context.action)
		if (section) scope.setTag('section', section)
		if (entityId) scope.setTag('entityId', entityId)

		scope.setContext('ui_error', {
			...context,
			feature,
			section,
			entityId,
			metadata,
			path: window.location.pathname,
		})

		scope.setFingerprint([
			'ui-error',
			context.source,
			feature || 'unknown',
			errorObj.name,
		])

		Sentry.captureException(errorObj)
	})
}
